<template>
  <div class="search">
    <div class="container">
      <button class="search__button" @click="router.go(-1)">
        <svg width="10" height="12" viewBox="0 0 10 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M1.53572 5.38596L6.24989 0.500372L7.42822 1.72155L3.30322 5.99655L7.42822 10.2716L6.24989 11.4927L1.53572 6.60714C1.3795 6.44519 1.29173 6.22556 1.29173 5.99655C1.29173 5.76755 1.3795 5.54792 1.53572 5.38596Z" fill="#2B3044"/>
        </svg>
        {{ t('buttons.otherProducts') }}
      </button>
      <h1 class="search__title">{{ name }}</h1>
    </div>
    <div class="search__main">
      <img class="search__main-bg" src="@images/storeBg.png" alt="bg">
      <div class="container">
        <div class="search__list" v-if="products.length > 0">
          <product-card
              v-for="product in products"
              :key="product.node.uuid"
              :product="product.node"
          />
          <div class="search__list-observer" ref="observer"></div>
        </div>
        <div class="search__list" v-else-if="isLoading">
          <product-card-skeleton v-for="idx in 12" :key="idx" />
        </div>
        <p v-else class="search__noProducts">{{ t('store.noProducts') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import ProductCardSkeleton from "@/components/skeletons/product-card-skeleton.vue";
import ProductCard from "@/components/cards/product-card.vue";
import {useI18n} from "vue-i18n";
import {Products} from "@/core/api/products.js";
import {onMounted, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useIntersectionObserver} from "@vueuse/core";

const {t, locale} = useI18n()
const route = useRoute()
const router = useRouter()

const isLoading = ref(false)
const observer = ref(null)

const { stop } = useIntersectionObserver(
  observer,
  ([{ isIntersecting }], observerElement) => {
    if (isIntersecting && pageInfo.value.hasNextPage) {
      getMoreProducts()
    }
  },
)

const cursorForAfter = ref('')
const name = ref(route.query['name'] ? (route.query['name']) : '')
const products = ref([])
const pageInfo = ref([])

const getProducts = async () => {
  isLoading.value = true
  cursorForAfter.value = '0'

  if (name.value) {
    const res = await Products.getProducts(
        `after: "${cursorForAfter.value}"` +
        `, first: 12` +
        `, name: "${name.value}"`
    )
    products.value = res.data?.products?.edges
    pageInfo.value = res.data?.products?.pageInfo

    isLoading.value = false
  } else {
    await router.push({
      name: 'home',
      params: {
        locale: localStorage.getItem('fpt-user-locale') || 'en-gb'
      }
    })
  }
}

const getMoreProducts = async () => {
  isLoading.value = true

  cursorForAfter.value = pageInfo.value.endCursor

  const res = await Products.getProducts(
      `after: "${cursorForAfter.value}"` +
      `, first: 12` +
      `, name: "${name.value}"`
  )
  products.value.push(...res.data.products.edges)
  pageInfo.value = res.data.products.pageInfo

  isLoading.value = false
}

watch(
    () => locale.value,
    async () => {
      products.value = [];
      await getProducts()
    }
)

onMounted( async () => {
  await getProducts()
})
</script>

<style lang="scss" scoped>
.search {
  padding-top: 175px;

  &__title {
    margin-bottom: 25px;
    font-size: 36px;
    font-family: Syne, sans-serif;
    font-weight: 700;
    background: linear-gradient(90deg, #1B90D3 0.5%, #04347D 44.83%, #2B3044 100%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  &__button {
    margin-bottom: 25px;
    border-radius: 10px;
    border: 1px solid #9AC3F9;
    background-color: $white;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);
    padding: 14px 7px;
    cursor: pointer;
    transition: 0.2s;

    color: #2B3044;
    font-family: Inter, sans-serif;
    font-size: 16px;
    font-weight: 500;

    &:hover {
      background-color: #ececec;
    }
  }

  &__main {
    background-color: rgba(157, 231, 255, 0.54);
    padding-block: 75px;
    position: relative;
    overflow: hidden;

    &-bg {
      width: 100%;
      position: absolute;
      left: 0;
      top: 250px;
    }
  }

  &__list {
    position: relative;
    z-index: 1;
    width: 100%;
    display: grid;
    grid-template-columns: repeat(3, 359px);
    justify-content: space-between;
    row-gap: 50px;

    &-observer {
      width: 100%;
      height: 10px;
      background-color: transparent;
    }
  }

  &__noProducts {
    text-align: center;
    font-size: 24px;
    font-weight: 500;
  }
}

@media (max-width: 1250px) {
  .search {
    &__list {
      grid-template-columns: repeat(2, 359px);
      column-gap: 50px;
      justify-content: center;
    }
  }
}

@media (max-width: 850px) {
  .search {
    &__list {
      grid-template-columns: repeat(1, 359px);
    }
  }
}
</style>